import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { MdMenuBook, MdCalendarToday, MdOpenInNew, MdAutoStories } from 'react-icons/md';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import theme from '../styles/theme';

const Wrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  color: #9ca3af;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const CourseCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover {
    box-shadow: 0 8px 20px rgba(79, 70, 229, 0.12);
    transform: translateY(-2px);
  }
`;

const CourseTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
`;

const CourseDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const MetaBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #374151;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 4px 10px;
`;

const Divider = styled.div`
  border-top: 1px solid #f3f4f6;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

const SharedDate = styled.div`
  font-size: 11px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 10px;
  background: ${theme.gradient};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
  &:active { opacity: 0.75; }
`;

const NoBatchBanner = styled.div`
  background: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 16px 20px;
  color: #92400e;
  font-size: 14px;
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 16px 20px;
  color: #b91c1c;
  font-size: 14px;
`;

const formatDate = (isoString) => {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoString;
  }
};

export default function SharedCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const courseInfo = AuthService.getCourseInfo();
  const batchId = courseInfo?.batchId ?? null;
  const batchName = courseInfo?.batchName ?? null;

  const fetchCourses = useCallback(async () => {
    if (!batchId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const { data } = await axiosInstance.get('/students/shared-courses', {
        params: { batchId },
      });
      setCourses(data.courses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const openCourse = (courseId) => {
    const targetUrl = `/app/shared-courses/${courseId}?batchId=${encodeURIComponent(batchId)}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const renderContent = () => {
    if (!batchId) {
      return (
        <NoBatchBanner>
          No active course selected. Please select a course from the dashboard to view shared study materials.
        </NoBatchBanner>
      );
    }

    if (loading) return <Loader />;

    if (error) return <ErrorBanner>{error}</ErrorBanner>;

    if (courses.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon><MdAutoStories /></EmptyIcon>
          <EmptyText>No study material courses have been shared with your batch yet.</EmptyText>
        </EmptyState>
      );
    }

    return (
      <Grid>
        {courses.map((course) => (
          <CourseCard key={course.id}>
            <CourseTitle>{course.title}</CourseTitle>

            {course.description && <CourseDescription>{course.description}</CourseDescription>}

            <MetaRow>
              <MetaBadge>
                <MdMenuBook size={13} />
                {course.lessonCount} {course.lessonCount === 1 ? 'lesson' : 'lessons'}
              </MetaBadge>
            </MetaRow>

            <Divider />

            <CardFooter>
              <SharedDate>
                <MdCalendarToday size={11} />
                Shared {formatDate(course.sharedAt)}
              </SharedDate>

              <OpenButton onClick={() => openCourse(course.id)}>
                <MdOpenInNew size={16} />
                Open Course
              </OpenButton>
            </CardFooter>
          </CourseCard>
        ))}
      </Grid>
    );
  };

  return (
    <StudentLayout>
      <Wrapper>
        <PageHeader>
          <Title>Shared Courses</Title>
          <Subtitle>
            {batchName
              ? `Study materials shared with ${batchName}`
              : 'Study material courses available for your batch'}
          </Subtitle>
        </PageHeader>
        {renderContent()}
      </Wrapper>
    </StudentLayout>
  );
}
